import { STATUSPAGE } from '@repo/ui';
import {
  GroupsCountUseCaseInterface,
  OngoingActivitiesByCourseUseCaseInterface,
  PositionCountForEntityUseCaseInterface,
  StudentsCountUseCaseInterface,
  StudentsRegisteredForOrganizationUseCaseInterface,
} from 'domain/usecases';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';

type GroupCountIcon = {
  iconName: string;
  size: number;
  classNameColorIcon: string;
};

export type GroupCount = {
  icon: GroupCountIcon;
  label: string;
  counter: string;
};

export const useEntity = () => {
  const { entitiesService } = useService();

  const [studentsCountResult, setStudentsCountResult] =
    useState<StudentsCountUseCaseInterface.Result>({
      activeInGroups: 0,
      activeWithoutGroup: 0,
      online: 0,
      withApp: 0,
      withoutApp: 0,
    });

  const [loadingStudentsCount, setLoadingStudentsCount] = useState<STATUSPAGE>(
    STATUSPAGE.LOADING,
  );

  const [loadingPositionCountForEntity, setLoadingPositionCountForEntity] =
    useState(STATUSPAGE.LOADING);

  const [positionCountForEntityResult, setPositionCountForEntityResult] =
    useState<PositionCountForEntityUseCaseInterface.Result>();

  const [groupsCountResult, setGroupsCountResult] =
    useState<GroupsCountUseCaseInterface.Result>({
      active: 0,
      archived: 0,
    });

  const [loadingGroupsCount, setLoadingGroupsCount] = useState(
    STATUSPAGE.LOADING,
  );

  const [loadingOngoingActivities, setLoadingOngoingActivities] = useState(
    STATUSPAGE.LOADING,
  );

  const [ongoingActivityByCourseResult, setOngoingActivityByCourseResult] =
    useState<OngoingActivitiesByCourseUseCaseInterface.Result[]>([
      { activitiesCount: 0, courseHashId: '', courseName: '' },
    ]);

  const [
    loadingStudentsRegisteredForOrganization,
    setLoadingStudentsRegisteredForOrganization,
  ] = useState(STATUSPAGE.LOADING);

  const [
    studentsRegisteredForOrganizationResult,
    setStudentsRegisteredForOrganizationResult,
  ] = useState<StudentsRegisteredForOrganizationUseCaseInterface.Result[]>([]);

  const studentsCountInfo = [
    {
      name: 'Estudantes Online',
      icon: {
        iconName: 'ri-group-line',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      value: new Intl.NumberFormat('pt-BR').format(studentsCountResult?.online),
    },
    {
      name: 'Estudantes em Grupos',
      icon: {
        iconName: 'ri-user-follow-line',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      value: new Intl.NumberFormat('pt-BR').format(
        studentsCountResult?.activeInGroups,
      ),
    },
    {
      name: 'Estudantes sem Grupos',
      icon: {
        iconName: 'ri-user-unfollow-line',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      value: new Intl.NumberFormat('pt-BR').format(
        studentsCountResult?.activeWithoutGroup,
      ),
    },
  ];

  const dataEntityRoleCount = [
    {
      icon: {
        iconName: 'ri-user-2-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Coordenadores',
      counter: new Intl.NumberFormat('pt-BR').format(
        positionCountForEntityResult?.coordinators,
      ),
    },

    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Professores',
      counter: new Intl.NumberFormat('pt-BR').format(
        positionCountForEntityResult?.teachers,
      ),
    },
    {
      icon: {
        iconName: 'ri-user-star-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Preceptores',
      counter: new Intl.NumberFormat('pt-BR').format(
        positionCountForEntityResult?.preceptors,
      ),
    },
    {
      icon: {
        iconName: 'ri-shield-user-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Assistentes/Auxiliares',
      counter: new Intl.NumberFormat('pt-BR').format(
        positionCountForEntityResult?.assistants +
          positionCountForEntityResult?.administratives,
      ),
    },
    {
      icon: {
        iconName: 'ri-account-box-fill',
        size: 30,
        classNameColorIcon: '#878A99',
      },
      label: 'Estudantes',
      counter: new Intl.NumberFormat('pt-BR').format(
        positionCountForEntityResult?.students,
      ),
    },
  ];

  const groupsCount: GroupCount[] = [
    {
      icon: {
        iconName: 'activity',
        size: 25,
        classNameColorIcon: '#00bcd4',
      },
      label: 'Grupos Ativos',
      counter: new Intl.NumberFormat('pt-BR').format(groupsCountResult?.active),
    },
    {
      icon: {
        iconName: 'archive',
        size: 30,
        classNameColorIcon: '#00bcd4',
      },
      label: 'Grupos Arquivados',
      counter: new Intl.NumberFormat('pt-BR').format(
        groupsCountResult?.archived,
      ),
    },
  ];

  const ongoingActivityByCourse = (ongoingActivityByCourseResult || [])
    .filter((item) => item.activitiesCount > 0)
    .map((item) => {
      return {
        x: item.courseName,
        y: item.activitiesCount,
      };
    });

  const studentsRegisteredForOrganizationFormate = (
    studentsRegisteredForOrganizationResult || []
  )?.map((item) => {
    return {
      photo: item?.photo,
      name: item?.name,
      course: item?.courseName,
      registrationTime: item?.registeredAt,
    };
  });

  const requestStudentsCount = useCallback(
    async (params: StudentsCountUseCaseInterface.Params) => {
      setLoadingStudentsCount(STATUSPAGE.LOADING);
      await entitiesService
        .studentsCount(params)
        .then((res) => {
          setStudentsCountResult(res);
        })
        .finally(() => {
          setLoadingStudentsCount(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestPositionCountForEntity = useCallback(
    async (params: PositionCountForEntityUseCaseInterface.Params) => {
      setLoadingPositionCountForEntity(STATUSPAGE.LOADING);
      await entitiesService
        .positionCountForEntity(params)
        .then((res) => {
          setPositionCountForEntityResult(res);
        })
        .finally(() => {
          setLoadingPositionCountForEntity(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGroupsCount = useCallback(
    async (params: GroupsCountUseCaseInterface.Params) => {
      setLoadingGroupsCount(STATUSPAGE.LOADING);
      await entitiesService
        .groupsCount(params)
        .then((res) => {
          setGroupsCountResult(res);
        })
        .finally(() => {
          setLoadingGroupsCount(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestOngoingActivitiesByCourse = useCallback(
    async (params: OngoingActivitiesByCourseUseCaseInterface.Params) => {
      setLoadingOngoingActivities(STATUSPAGE.LOADING);
      await entitiesService
        .ongoingActivitiesByCourse(params)
        .then((res) => {
          setOngoingActivityByCourseResult(res);
        })
        .finally(() => {
          setLoadingOngoingActivities(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestStudentsRegisteredForOrganization = useCallback(
    async (
      params: StudentsRegisteredForOrganizationUseCaseInterface.Params,
    ) => {
      setLoadingStudentsRegisteredForOrganization(STATUSPAGE.LOADING);
      await entitiesService
        .studentsRegisteredForOrganization(params)
        .then((res) => {
          setStudentsRegisteredForOrganizationResult(res);
        })
        .finally(() => {
          setLoadingStudentsRegisteredForOrganization(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    //
    requestStudentsRegisteredForOrganization,
    studentsRegisteredForOrganizationResult,
    loadingStudentsRegisteredForOrganization,
    studentsRegisteredForOrganizationFormate,
    //
    requestOngoingActivitiesByCourse,
    ongoingActivityByCourse,
    loadingOngoingActivities,
    ongoingActivityByCourseResult,
    //
    requestGroupsCount,
    groupsCount,
    groupsCountResult,
    loadingGroupsCount,
    //
    requestPositionCountForEntity,
    dataEntityRoleCount,
    positionCountForEntityResult,
    loadingPositionCountForEntity,
    //
    requestStudentsCount,
    studentsCountInfo,
    studentsCountResult,
    loadingStudentsCount,
  };
};
